import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import i18n from '../i18n';
import { isChunkLoadError } from '../lib/appUpdateCore';
import { reloadOnceForChunkError } from '../lib/appUpdate';

// i18n.t's overloads trip this TypeScript version on plain string keys (same cast as translateIn in i18n/index.ts).
const t = (key: string) => (i18n.t as unknown as (k: string) => string)(key);

/**
 * Last line of defence (2026-10-04): without a boundary any render error — above all a chunk
 * that no longer exists after a deploy — unmounted the whole app and left a white screen until
 * the user killed it. A chunk failure reloads once; anything else shows a reload screen.
 * i18n.t (not the hook): the boundary must render even when React context is what broke.
 */
export default class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('AppErrorBoundary', error, info.componentStack);
    if (isChunkLoadError(error)) reloadOnceForChunkError();
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-4">
          <h1 className="text-xl font-semibold text-foreground">{t('app.crashTitle')}</h1>
          <p className="text-muted-foreground">{t('app.crashBody')}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold"
          >
            {t('app.crashReload')}
          </button>
        </div>
      </div>
    );
  }
}
